import * as React from "react";
import {
	Box,
	Checkbox,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	OutlinedInput,
	Radio,
	RadioGroup,
	Select,
	Stack,
	Switch,
	Typography,
} from "@mui/material";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { Option } from "../core/option";
import { CustomDatePicker } from "../customDatePicker/custom-date-picker";
import { CustomFormHelperDescription } from "../customFormHelper/custom-form-helper-description";
import { CustomFormHelperError } from "../customFormHelper/custom-form-helper-error";
import { CustomSlider } from "../customSlider/customSlider";
import { SecretField } from "./secret-field";
import { ServiceField } from "./service-field";
import { ServicePluginField } from "./service-plugin-field";
import { TextAreaWithExpand } from "./text-area-with-field";
import { DynamicFieldType, FieldType } from "./types";

const compareValues = (value1, value2) => {
	if (Array.isArray(value1) && Array.isArray(value2)) {
		return value1.some((val) => value2.includes(val));
	}

	if (Array.isArray(value1)) {
		return value1.includes(value2);
	}

	if (Array.isArray(value2)) {
		return value2.includes(value1);
	}

	return value1 === value2;
};

interface DynamicFieldProps {
	gridSize?: any;
	control: any;
	type: DynamicFieldType;
	name: string;
	label: string;
	hideLabel?: boolean;
	hideDescription?: boolean;
	dynamicField: FieldType;
	onChange?: any;
	dependsOn?: any;
	formName?: string;
	disabled?: boolean;
	labelButton?: React.ReactElement;
}

function getObject(values, name) {
	return name.split(".").reduce((acc, curr) => acc?.[curr], values);
}

function shouldRenderField(dynamicField, values, formName) {
	if (formName) values = getObject(values, formName);

	return (
		dynamicField?.dependsOn?.every((dep) => {
			let val = values?.[dep.field];

			if (val === undefined) val = "";

			const result = compareValues(val, dep.value);

			return dep.reverse ? !result : result;
		}) ?? true
	);
}

function shouldRenderOption(option, values, formName) {
	return shouldRenderField(option, values, formName);
}

export function DynamicField({
	gridSize,
	control,
	type,
	name,
	label,
	dynamicField,
	onChange,
	formName,
	hideLabel,
	hideDescription,
	disabled,
	labelButton,
}: DynamicFieldProps) {
	const { control: formControl, unregister } = useFormContext();

	const values = useWatch({ control: formControl });

	const visible = shouldRenderField(dynamicField, values, formName);

	const selectRef = React.useRef<HTMLDivElement>(null);
	const [selectWidth, setSelectWidth] = React.useState<number>();

	React.useEffect(() => {
		if (!visible) {
			unregister(name);
		}
	}, [visible, name, unregister]);

	React.useEffect(() => {
		if (selectRef.current && visible) {
			const updateWidth = () => {
				setSelectWidth(selectRef.current?.offsetWidth);
			};
			updateWidth();
			window.addEventListener("resize", updateWidth);
			return () => window.removeEventListener("resize", updateWidth);
		}
	}, [visible]);

	if (!visible) {
		return null;
	}

	return (
		<Grid container spacing={2} sx={{ width: "100%" }}>
			<Grid size={gridSize} sx={{ flex: 1 }}>
				<Controller
					control={control}
					name={name}
					defaultValue={dynamicField.value}
					key={name}
					rules={{
						required: dynamicField.required ? "This field is required" : false,
					}}
					render={({ field, fieldState }) => {
						return (
							<FormControl
								error={Boolean(fieldState.error)}
								fullWidth
								required={dynamicField.required}
								disabled={disabled}
							>
								{label &&
									!hideLabel &&
									type !== DynamicFieldType.CHECKBOX &&
									type !== DynamicFieldType.SERVICE &&
									type !== DynamicFieldType.PLUGIN_SERVICE && (
										<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
											<InputLabel sx={{ position: "relative", zIndex: 0 }}>{label}</InputLabel>
											{labelButton}
										</Box>
									)}

								{type === DynamicFieldType.TEXT && !dynamicField.multiline && (
									<OutlinedInput
										{...field}
										value={field.value || ""}
										placeholder={`Enter ${label}`}
										type={dynamicField.format}
										onChange={(event) => {
											onChange?.(event.target.value);
											field.onChange(event);
										}}
										sx={{ mt: 1 }}
									/>
								)}
								{type === DynamicFieldType.RADIO && (
									<RadioGroup
										{...field}
										value={field.value || ""}
										onChange={(event) => {
											onChange?.(event.target.value);
											field.onChange(event);
										}}
									>
										{dynamicField.options?.map((option) => (
											<FormControlLabel
												key={option.value}
												value={option.value}
												control={<Radio />}
												label={option.label}
											/>
										))}
									</RadioGroup>
								)}
								{type === DynamicFieldType.CHECKBOX && (
									<Stack direction="column">
										<Stack direction="row" alignItems="center" gap={1}>
											<Checkbox
												{...field}
												sx={{ alignSelf: "flex-start", mt: "0 !important" }}
												checked={field.value || false}
												onChange={(event) => {
													onChange?.(event.target.value);
													field.onChange(event);
												}}
											/>
											<InputLabel sx={{ position: "relative", zIndex: 0 }}>{label}</InputLabel>
										</Stack>
										<Stack>
											{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
											<Box sx={{ ml: 2.25, mt: -0.5 }}>
												{dynamicField.description && <CustomFormHelperDescription text={dynamicField.description} />}
											</Box>
										</Stack>
									</Stack>
								)}
								{(type === DynamicFieldType.TEXT_AREA || dynamicField.multiline) && (
									<TextAreaWithExpand
										field={field}
										label={label}
										withCustomFields={dynamicField.withCustomFields || false}
										placeholder={dynamicField.placeholder}
										summaryInfo={dynamicField?.summaryInfo}
									/>
								)}
								{type === DynamicFieldType.NUMBER && (
									<OutlinedInput
										{...field}
										value={field.value === 0 ? 0 : field.value || ""}
										placeholder={`Enter ${label}`}
										type="number"
										inputProps={{ min: dynamicField.min, max: dynamicField.max, step: dynamicField.step }}
										onChange={(event) => {
											if (event.target.value === "") {
												onChange?.(null);
												field.onChange(null);
												return;
											}
											onChange?.(Number(event.target.value));
											field.onChange(Number(event.target.value));
										}}
										sx={{ mt: 1 }}
									/>
								)}
								{type === DynamicFieldType.SLIDER && <CustomSlider field={field} dynamicField={dynamicField} />}
								{type === DynamicFieldType.BOOLEAN && (
									<Switch
										{...field}
										checked={field.value || false}
										onChange={(event) => {
											onChange?.(event.target.value);
											field.onChange(event);
										}}
									/>
								)}
								{type === DynamicFieldType.SELECT && (
									<Select
										{...field}
										ref={selectRef}
										defaultValue={dynamicField.defaultValue || ""}
										value={field.value === undefined || dynamicField.showPlaceholder ? "" : field.value}
										onChange={(event) => {
											onChange?.(event.target.value);
											field.onChange(event);
										}}
										renderValue={(value) => {
											if (
												dynamicField.isIndex &&
												value &&
												dynamicField.options?.find((opt) => opt.value === value)?.label === ""
											) {
												return "";
											}
											return (
												dynamicField.options?.find((opt) => opt.value === value)?.label ||
												value ||
												dynamicField.placeholder ||
												`Select ${label}`
											);
										}}
										sx={{
											mt: 1,
											color:
												field.value === "" || field.value === undefined
													? "var(--mui-palette-text-secondary)"
													: "inherit",
										}}
										MenuProps={{
											PaperProps: {
												sx: {
													width: selectWidth ? `${selectWidth}px !important` : "auto",
													maxHeight: "300px !important",
													minWidth: selectWidth ? `${selectWidth}px !important` : "auto",
												},
											},
										}}
									>
										<Option
											value=""
											sx={{
												color: "var(--mui-palette-text-secondary)",
												fontStyle: "italic",
												fontSize: "16px !important",
												minHeight: "36px !important",
												whiteSpace: "pre-wrap !important",
											}}
										>
											{dynamicField.placeholder || `Select ${label}`}
										</Option>
										{dynamicField?.options?.map((option) => {
											if (!shouldRenderOption(option, values, formName)) {
												if (field.value == option.value) {
													field.onChange("");
												}
												return null;
											}

											const optionDescription = option?.description;

											if (
												field.value &&
												!shouldRenderOption(
													dynamicField?.options?.find((opt) => opt.value === field.value),
													values,
													formName
												)
											) {
												field.onChange("");
											}

											return (
												<Option
													key={option.value}
													value={option.value}
													sx={{
														fontSize: "16px !important",
														minHeight: "36px !important",
														whiteSpace: "pre-wrap !important",
														display: "flex",
														flexDirection: "column",
														alignItems: "flex-start",
														gap: 0,
													}}
												>
													{option.label}
													<br />
													{option.description && (
														<Typography variant="body2" color="text.secondary" mt={-0.5} fontSize={14}>
															{option.description}
														</Typography>
													)}

													{/* {optionDescription && (
														<Tooltip
															title={optionDescription}
															arrow
															placement="top"
															slotProps={{ tooltip: { sx: { lineHeight: 1.8, maxWidth: 400 } } }}
														>
															<Box
																sx={{
																	display: "inline-flex",
																	ml: 1,
																}}
															>
																<InfoIcon size={20} color="var(--mui-palette-text-secondary)" />
															</Box>
														</Tooltip>
													)} */}
												</Option>
											);
										})}
									</Select>
								)}
								{type === DynamicFieldType.DATE && (
									<CustomDatePicker
										{...field}
										label={""}
										value={field.value || dynamicField.defaultValue}
										setValue={(value) => {
											onChange?.(value);
											field.onChange(value);
										}}
										placeholder={dynamicField.placeholder}
										error={Boolean(fieldState.error)}
										fieldStyles={{ width: { xs: "100%", md: "250px" } }}
									/>
								)}
								{type === DynamicFieldType.SECRET && (
									<SecretField field={field} dynamicField={dynamicField} onChange={onChange} label={label} />
								)}
								{type === DynamicFieldType.SERVICE && (
									<ServiceField
										field={field}
										dynamicField={dynamicField}
										service={dynamicField.serviceType || ""}
										onChange={onChange}
										label={label}
									/>
								)}

								{type === DynamicFieldType.PLUGIN_SERVICE && (
									<ServicePluginField
										field={field}
										dynamicField={dynamicField}
										service={dynamicField.serviceType || ""}
										onChange={onChange}
										label={label}
									/>
								)}

								{/* Error and description */}
								{type !== DynamicFieldType.CHECKBOX &&
									type !== DynamicFieldType.SERVICE &&
									!hideDescription &&
									type !== DynamicFieldType.PLUGIN_SERVICE && (
										<>
											{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
											{dynamicField.description && <CustomFormHelperDescription text={dynamicField.description} />}
										</>
									)}
							</FormControl>
						);
					}}
				/>
			</Grid>
		</Grid>
	);
}
